import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { ShieldCheck, Cpu, ScanLine, ArrowRight } from "lucide-react";

const SCAN_BG = "https://images.unsplash.com/photo-1762279388956-1c098163a2a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxmdXR1cmlzdGljJTIwbmV0d29yayUyMGFic3RyYWN0JTIwbmVvbiUyMGJsdWV8ZW58MHx8fHwxNzg2OTg0MDU5fDA&ixlib=rb-4.1.0&q=85";

export default function Auth() {
  const { login, register } = useAuth();
  const { t, tError, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "developer" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(tError(formatApiError(err.response?.data?.detail)) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const roleLabels = { developer: t("auth.developer"), seller: t("auth.seller"), buyer: t("auth.buyer") };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#05050A]">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-border overflow-hidden grid-texture">
        <img src={SCAN_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-screen" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/60 to-transparent" />
        <div className="relative z-10"><Logo size={40} /></div>
        <div className="relative z-10 max-w-md">
          <div className="font-mono text-xs text-primary tracking-[0.3em] mb-4">{t("common.cognitiveSystem")}</div>
          <h1 className="font-display font-extrabold text-4xl xl:text-5xl leading-[1.05] mb-6">
            {t("auth.uploadVerifyPublish")}
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed">{t("auth.heroDesc")}</p>
          <div className="grid grid-cols-3 gap-4 mt-10">
            {[
              { icon: ScanLine, t: t("auth.technical") },
              { icon: Cpu, t: t("auth.cognitive") },
              { icon: ShieldCheck, t: t("auth.governance") },
            ].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 * i }}
                className="border border-border p-4 bg-[#0C0D14]/70">
                <f.icon className="w-5 h-5 text-primary mb-3" />
                <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{f.t}</div>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="relative z-10 font-mono text-[10px] text-muted-foreground tracking-widest uppercase">
          {t("common.autonomous")}
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute top-6 right-6 flex border border-border" data-testid="lang-switch-auth">
          {["it", "en"].map((l) => (
            <button key={l} data-testid={`lang-auth-${l}`} onClick={() => setLang(l)}
              className={`px-2.5 py-1 font-mono text-[11px] uppercase transition-colors ${lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo size={36} /></div>
          <h2 className="font-display font-bold text-2xl mb-1">
            {mode === "login" ? t("auth.access") : t("auth.registerNode")}
          </h2>
          <p className="text-muted-foreground text-sm mb-8">
            {mode === "login" ? t("auth.accessSub") : t("auth.registerSub")}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("auth.name")}</Label>
                <Input data-testid="auth-name" className="rounded-none bg-[#0C0D14] border-border focus-visible:ring-primary mt-1.5"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
            )}
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("auth.email")}</Label>
              <Input data-testid="auth-email" type="email" className="rounded-none bg-[#0C0D14] border-border focus-visible:ring-primary mt-1.5"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("auth.password")}</Label>
              <Input data-testid="auth-password" type="password" className="rounded-none bg-[#0C0D14] border-border focus-visible:ring-primary mt-1.5"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            </div>
            {mode === "register" && (
              <div>
                <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("auth.role")}</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {["developer", "seller", "buyer"].map((r) => (
                    <button type="button" key={r} data-testid={`role-${r}`} onClick={() => setForm({ ...form, role: r })}
                      className={`py-2 font-mono text-[11px] uppercase tracking-wider border transition-colors ${
                        form.role === r ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"
                      }`}>
                      {roleLabels[r]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div data-testid="auth-error" className="border border-[#FF2A2A]/50 bg-[#FF2A2A]/10 text-[#FF2A2A] text-xs font-mono p-3">
                {error}
              </div>
            )}

            <Button data-testid="auth-submit" type="submit" disabled={busy}
              className="w-full rounded-none bg-primary text-primary-foreground font-mono uppercase tracking-wider hover:bg-primary/90 active:scale-95 transition-transform group">
              {busy ? t("auth.processing") : mode === "login" ? t("auth.authenticate") : t("auth.register")}
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button data-testid="toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="font-mono text-xs text-muted-foreground hover:text-primary transition-colors">
              {mode === "login" ? t("auth.toRegister") : t("auth.toLogin")}
            </button>
          </div>

          {mode === "login" && (
            <div className="mt-6 border border-border p-3 font-mono text-[11px] text-muted-foreground">
              <span className="text-primary">{t("auth.demo")}:</span> admin@rexxpons.net / RexxAdmin123
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
